/**
 * This method returns the person type for A/B testing
 *
 * @param {string} page_type - the type of page to retrieve the person type for
 * @return {string | null} the person type for the specified page, or null if it does not exist
 */
export const getPersonType = (page_type: string): string | null => {
  return localStorage.getItem(page_type) || null;
};

/**
 * Sets the person type for A/B testing.
 *
 * @param {string} personType - the type of person to set
 * @param {string} page_type - the type of page to set the person type for
 * @param {boolean} [force=false] - a flag indicating whether to force setting the person type
 */
export const setPersonType = (
  personType: string,
  page_type: string,
  force: boolean = false
): void => {
  if (!getPersonType(page_type) || force) {
    localStorage.setItem(page_type, personType);
  }
};

/**
 * Returns a random person type. To start with it returns either "A" or "B".
 *
 * @return {"A" | "B"} the randomly generated person type (either "A" or "B")
 */
export const getRandomPersonType = (): "A" | "B" => {
  return Math.floor(Math.random() * 10) % 2 === 0 ? "A" : "B";
};

/**
 * Sets a random person type for A/B testing based on the page type.
 *
 * @param page_type - the type of page to set the person type for
 * @return the randomly generated person type (either "A" or "B")
 */
export const setRandomPersonType = (page_type: string): "A" | "B" => {
  const randomPersonType: "A" | "B" = getRandomPersonType();
  setPersonType(randomPersonType, page_type);
  return randomPersonType;
};

/**
 * Retrieves or sets a person type for A/B testing based on the provided page type.
 *
 * @param {string} pageType - The type of page to get or set the person type for.
 * @param {boolean} [force=false] - A flag indicating whether to force setting a new person type.
 * @returns {string | null} The existing person type if it exists, otherwise the new person type.
 */
export const getOrSetPersonType = (
  pageType: string,
  force: boolean = false
): "A" | "B" | null => {
  const existingPersonType: string | null = getPersonType(pageType);
  const randomPersonType: "A" | "B" | null = setRandomPersonType(pageType);

  if (!existingPersonType || force) {
    return randomPersonType;
  }

  return existingPersonType as "A" | "B" | null;
};
